/**
 * Constantes partilhadas por toda a aplicação.
 *
 * ATENÇÃO: o tsconfig deste projeto tem `erasableSyntaxOnly: true`, o que
 * PROÍBE `enum` de TypeScript. O padrão obrigatório é um objeto `as const`
 * mais um tipo derivado dele:
 *
 *     export const X = { A: "a", B: "b" } as const;
 *     export type X = (typeof X)[keyof typeof X];
 *
 * Isto compila para JavaScript puro (o objeto), continua a dar união de
 * literais no lado dos tipos, e é o que os módulos de domínio devem seguir.
 */

/** Tipos de entidade registados na supertabela `item`. */
export const ENTITY_TYPES = {
  TAREFA: "tarefa",
  REQUISITO: "requisito",
  LEGADO: "legado",
  OBJETO_DADOS: "objeto_dados",
  BACKLOG: "backlog",
  GAP: "gap",
  PARKING_LOT: "parking_lot",
  RISCO_ISSUE: "risco_issue",
  CHANGE_REQUEST: "change_request",
  RICEFW: "ricefw",
  CONFIGURACAO: "configuracao",
  BUG: "bug",
  WORKSHOP: "workshop",
  DECISAO_CHAVE: "decisao_chave",
} as const;
export type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

/** Papéis da aplicação — espelha o enum `public.app_role` no Postgres. */
export const PAPEIS = {
  ADMIN: "admin",
  GESTOR: "gestor",
  CONSULTOR: "consultor",
  LEITOR: "leitor",
} as const;
export type Papel = (typeof PAPEIS)[keyof typeof PAPEIS];

/** Ações de permissão — espelha as colunas can_* de `user_module_access`. */
export const PERMISSION_ACTIONS = {
  READ: "read",
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
} as const;
export type PermissionAction =
  (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS];

/** Esteiras de gates. Cada uma tem o seu conjunto de etapas em `workflow_stage`. */
export const ESTEIRAS = {
  RICEFW: "ricefw",
  CONFIG: "config",
  ETL: "etl",
} as const;
export type Esteira = (typeof ESTEIRAS)[keyof typeof ESTEIRAS];

/**
 * Módulos da aplicação.
 *
 * `slug` é o segmento de rota e ao mesmo tempo a chave usada em
 * `user_module_access.module` — manter os dois iguais evita uma tabela de
 * tradução entre o que a sidebar mostra e o que a RLS verifica.
 */
export interface ModuloDef {
  slug: string;
  titulo: string;
  grupo: "programa" | "analise" | "administracao";
  entityType?: EntityType;
}

// A ORDEM é a do fluxo do programa, pedida a 14/09 (slide 1): do plano ao
// levantamento, à especificação, à construção, e por fim ao que corre mal.
// Os nomes e os ícones ficam — o mesmo slide pede para os manter.
export const MODULOS: readonly ModuloDef[] = [
  { slug: "cronograma", titulo: "Cronograma", grupo: "programa", entityType: ENTITY_TYPES.TAREFA },
  { slug: "workshops", titulo: "Workshops", grupo: "programa", entityType: ENTITY_TYPES.WORKSHOP },
  { slug: "requisitos", titulo: "Requisitos", grupo: "programa", entityType: ENTITY_TYPES.REQUISITO },
  { slug: "gaps", titulo: "GAPs", grupo: "programa", entityType: ENTITY_TYPES.GAP },
  { slug: "decisoes-chave", titulo: "Decisões Chave", grupo: "programa", entityType: ENTITY_TYPES.DECISAO_CHAVE },
  { slug: "backlog", titulo: "Backlog", grupo: "programa", entityType: ENTITY_TYPES.BACKLOG },
  { slug: "parking-lot", titulo: "Parking Lot", grupo: "programa", entityType: ENTITY_TYPES.PARKING_LOT },
  { slug: "change-requests", titulo: "Change Requests", grupo: "programa", entityType: ENTITY_TYPES.CHANGE_REQUEST },
  // O rótulo passa a "Desenvolvimento", como o relatório de 27/08 pede. O
  // SLUG fica: vive também em `user_module_access.module` e em
  // `usuario_visoes.modulo`, e trocá-lo tirava o acesso a quem o tinha, sem
  // erro nenhum.
  { slug: "ricefws", titulo: "Desenvolvimento", grupo: "programa", entityType: ENTITY_TYPES.RICEFW },
  { slug: "configuracoes", titulo: "Configurações", grupo: "programa", entityType: ENTITY_TYPES.CONFIGURACAO },
  { slug: "dados", titulo: "Dados", grupo: "programa", entityType: ENTITY_TYPES.OBJETO_DADOS },
  { slug: "legados", titulo: "Legados", grupo: "programa", entityType: ENTITY_TYPES.LEGADO },
  { slug: "bugs", titulo: "BUGs & Defeitos", grupo: "programa", entityType: ENTITY_TYPES.BUG },
  { slug: "riscos-issues", titulo: "Riscos & Issues", grupo: "programa", entityType: ENTITY_TYPES.RISCO_ISSUE },
  { slug: "dashboard", titulo: "Dashboard", grupo: "analise" },
] as const;

/** Domínios do catálogo `status_definition`. */
export const DOMINIOS_CATALOGO = {
  COMPLEXIDADE: "complexidade",
  PRIORIDADE: "prioridade",
  PROBABILIDADE: "probabilidade",
  IMPACTO: "impacto",
  GRAU_RISCO: "grau_risco",
  RESPOSTA_RISCO: "resposta_risco",
  TIPO_RICEFW: "tipo_ricefw",
  VALUE_CHAIN: "value_chain",
  STATUS_GATE: "status_gate",
} as const;
export type DominioCatalogo =
  (typeof DOMINIOS_CATALOGO)[keyof typeof DOMINIOS_CATALOGO];

/**
 * Cores de farol permitidas em `status_definition.cor`.
 *
 * A base de dados guarda o NOME do token (ex.: "farol-verde"), nunca um
 * hexadecimal — as cores em si vivem em `src/index.css`. Retematizar o
 * produto é editar o CSS, não fazer UPDATE em centenas de linhas.
 */
export const CORES_FAROL = [
  "farol-verde",
  "farol-amarelo",
  "farol-vermelho",
  "farol-azul",
  "farol-cinza",
  "farol-roxo",
  "farol-cinza-claro",
  "farol-celeste",
  "farol-verde-escuro",
  "farol-ouro",
] as const;
export type CorFarol = (typeof CORES_FAROL)[number];
